/** Service functions for the on-demand catalog-generation flow
 *  (`/admin/catalog/*`, R3 §5). Kept separate from `adminModelsService`
 *  (per-model CRUD/lifecycle) so the job/constraints surface has one home.
 */

import { apiClient } from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import type {
  CatalogCancelResponse,
  CatalogCommitResponse,
  CatalogConstraints,
  CatalogConstraintsBundle,
  CatalogGenerateRequest,
  CatalogGenerateResponse,
  CatalogJobListResponse,
  CatalogJobResponse,
} from '@/api/types';

export const adminCatalogService = {
  // --- Generation ---
  generate: (body: CatalogGenerateRequest) =>
    apiClient
      .post<CatalogGenerateResponse>(API_ENDPOINTS.ADMIN_CATALOG.GENERATE, body)
      .then((r) => r.data),
  getJob: (id: string) =>
    apiClient
      .get<CatalogJobResponse>(API_ENDPOINTS.ADMIN_CATALOG.JOB(id))
      .then((r) => r.data),
  listJobs: (params?: { job_type?: string; status?: string; skip?: number; limit?: number }) =>
    apiClient
      .get<CatalogJobListResponse>(API_ENDPOINTS.ADMIN_CATALOG.JOBS, { params })
      .then((r) => r.data),
  commitJob: (id: string) =>
    apiClient
      .post<CatalogCommitResponse>(API_ENDPOINTS.ADMIN_CATALOG.JOB_COMMIT(id))
      .then((r) => r.data),
  cancelJob: (id: string) =>
    apiClient
      .post<CatalogCancelResponse>(API_ENDPOINTS.ADMIN_CATALOG.JOB_CANCEL(id))
      .then((r) => r.data),

  // --- On-demand bulk collections (tracked jobs, R3 amendment 15) ---
  enrich: () =>
    apiClient
      .post<CatalogGenerateResponse>(API_ENDPOINTS.ADMIN_CATALOG.ENRICH)
      .then((r) => r.data),
  evalRefresh: () =>
    apiClient
      .post<CatalogGenerateResponse>(API_ENDPOINTS.ADMIN_CATALOG.EVAL_REFRESH)
      .then((r) => r.data),

  // --- Constraints ---
  getConstraints: () =>
    apiClient
      .get<CatalogConstraintsBundle>(API_ENDPOINTS.ADMIN_CATALOG.CONSTRAINTS)
      .then((r) => r.data),
  putConstraints: (body: Partial<CatalogConstraints>) =>
    apiClient
      .put<CatalogConstraints>(API_ENDPOINTS.ADMIN_CATALOG.CONSTRAINTS, body)
      .then((r) => r.data),
};
